import appletManifest from "./applet-manifest.generated.json";

const RAW_APPLETS = process.env.REACT_APP_APPLETS ?? "all";

export const ENABLED_APPLETS = RAW_APPLETS.split(",")
  .map(value => value.trim())
  .filter(Boolean);

export function isAppletEnabled(applet: string): boolean {
  return ENABLED_APPLETS.includes("all") || ENABLED_APPLETS.includes(applet);
}

export function areAnyAppletsEnabled(applets: string[]): boolean {
  return applets.some(isAppletEnabled);
}

// Meeting decision: Chat + RAG + Workspaces are base platform functionality.
const BASE_FRONTEND_APPS = new Set(["chat", "workspaces"]);

export function isBaseChatPlatformEnabled(): boolean {
  return true;
}

const APP_ID_TO_PAGES: Record<string, string[]> = {
  talent: ["talent-intelligence", "role-builder", "reference-checks", "candidate-thank-you-videos"],
  chat: ["chat"],
  workspaces: ["workspaces"],
  sow: ["sow"],
  "content-research": ["content-research"],
  "prd-generator": ["prd-generator"],
  "microsite-generator": ["microsite-generator"],
  microsite_generator: ["microsite-generator"],
  client_opportunity_ranker: ["client-opportunity-ranker"],
  "client-opportunity-ranker": ["client-opportunity-ranker"],
  opportunity_diagnostic_intelligence: ["opportunity-diagnostic-intelligence"],
  "opportunity-diagnostic-intelligence": ["opportunity-diagnostic-intelligence"],
  pulse: ["pulse"],
  adoption: ["adoption"],
  bv_reviews: [
    "bv-reviews-studio",
    "bv-reviews-insights",
    "bv-reviews-library",
    "bv-reviews-settings",
  ],
  "bv-reviews": [
    "bv-reviews-studio",
    "bv-reviews-insights",
    "bv-reviews-library",
    "bv-reviews-settings",
  ],
  executive_command_center: ["executive-command-center"],
  "executive-command-center": ["executive-command-center"],
  cashflow: ["cashflow"],
  cashflow_v2: ["cashflow_v2"],
  catalog: ["catalog"],
  ttc_data_products: ["ttc-data-products"],
  "ttc-data-products": ["ttc-data-products"],
  close_calendar: ["close_calendar", "close_calendar_rules"],
  "close-calendar": ["close_calendar", "close_calendar_rules"],
  bank_reconciliation: ["bank_reconciliation"],
  "bank-reconciliation": ["bank_reconciliation"],
  ap_invoice_agent: ["ap-invoice-agent"],
  "ap-invoice-agent": ["ap-invoice-agent"],
  ap_cutoff_enforcer: ["ap-cutoff-enforcer"],
  "ap-cutoff-enforcer": ["ap-cutoff-enforcer"],
  payroll_entry: ["payroll-entry"],
  "payroll-entry": ["payroll-entry"],
  judgmental_accruals: ["judgmental-accruals"],
  "judgmental-accruals": ["judgmental-accruals"],
  lag_accruals: ["lag_accruals"],
  "lag-accruals": ["lag_accruals"],
  variable_lag_accruals: ["variable-lag-accruals"],
  "variable-lag-accruals": ["variable-lag-accruals"],
  asc_606_revenue_waterfall: ["asc-606-revenue-waterfall"],
  "asc-606-revenue-waterfall": ["asc-606-revenue-waterfall"],
  deferred_revenue_scheduler: ["deferred-revenue-scheduler"],
  "deferred-revenue-scheduler": ["deferred-revenue-scheduler"],
  subledger_revenue_reconciler: ["subledger-revenue-reconciler"],
  "subledger-revenue-reconciler": ["subledger-revenue-reconciler"],
  cloud_accruals: ["cloud-accruals"],
  "cloud-accruals": ["cloud-accruals"],
  contract_automation: ["contract-automation"],
  "contract-automation": ["contract-automation"],
  "deal-intelligence": ["coming-soon"],
  "rfp-responder": ["coming-soon"],
  "customer-health": ["coming-soon"],
  "ma-analyst": ["coming-soon"],
  "spend-optimizer": ["coming-soon"],
  "regulatory-radar": ["coming-soon"],
  "onboarding-copilot": ["coming-soon"],
  "competitive-intel": ["coming-soon"],
  "campaign-optimizer": ["coming-soon"],
  "contract-hub": ["coming-soon"],
  "it-service-desk": ["coming-soon"],
  "supply-chain": ["coming-soon"],
  "product-insights": ["coming-soon"],
  "eliza-engage": ["coming-soon"],
  "incident-commander": ["coming-soon"],
  "partner-intelligence": ["coming-soon"],
  "board-report-generator": ["coming-soon"],
  "knowledge-miner": ["coming-soon"],
  "brand-guardian": ["coming-soon"],
};

const APPLET_TO_PAGES: Record<string, string[]> =
  appletManifest.applet_to_pages as Record<string, string[]>;

const ENABLED_PAGES = (() => {
  if (ENABLED_APPLETS.includes("all")) {
    return null;
  }

  const pages = new Set<string>();
  for (const applet of ENABLED_APPLETS) {
    const manifestPages = APPLET_TO_PAGES[applet];
    if (!manifestPages) {
      continue;
    }
    for (const page of manifestPages) {
      pages.add(page);
    }
  }
  return pages;
})();

export function isFrontendPageEnabled(pageKey: string): boolean {
  if (ENABLED_PAGES === null) {
    return true;
  }
  return ENABLED_PAGES.has(pageKey);
}

export function isFrontendAppEnabled(appId: string): boolean {
  if (BASE_FRONTEND_APPS.has(appId)) {
    return true;
  }

  const mappedPages = APP_ID_TO_PAGES[appId];
  if (!mappedPages) {
    // Unknown app ids default to hidden for modular builds.
    return false;
  }
  return mappedPages.some(isFrontendPageEnabled);
}
